import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    inloopId: String,
    phaseId: String,
    reorderUrl: String
  }

  connect() {
    if (!this.hasPhaseIdValue) {
      const phaseContainer = this.element.closest('[data-phase-id]')
      if (phaseContainer) {
        this.phaseIdValue = phaseContainer.dataset.phaseId
      }
    }
    // Use MutationObserver to detect when activities are added
    const observer = new MutationObserver(() => {
      // Update when DOM changes
    })

    observer.observe(this.element, { childList: true, subtree: true })

    // Throttle for instant reorder to prevent too many updates
    this.lastReorderTime = 0
    this.reorderThrottle = 50 // ms

    this.boundDragStart = this.dragStart.bind(this)
    this.boundDragEnd = this.dragEnd.bind(this)
    this.boundDragOver = this.dragOver.bind(this)
    this.boundDragLeave = this.dragLeave.bind(this)
    this.boundDrop = this.drop.bind(this)

    this.element.addEventListener('dragstart', this.boundDragStart, true)
    this.element.addEventListener('dragend', this.boundDragEnd, true)
    this.element.addEventListener('dragover', this.boundDragOver)
    this.element.addEventListener('dragleave', this.boundDragLeave)
    this.element.addEventListener('drop', this.boundDrop)
  }

  disconnect() {
    this.element.removeEventListener('dragstart', this.boundDragStart, true)
    this.element.removeEventListener('dragend', this.boundDragEnd, true)
    this.element.removeEventListener('dragover', this.boundDragOver)
    this.element.removeEventListener('dragleave', this.boundDragLeave)
    this.element.removeEventListener('drop', this.boundDrop)
  }

  dragStart(event) {
    // The target should be the handle (has draggable="true")
    // Check if event.target itself has draggable or find closest draggable element
    let handle = event.target
    if (!handle.draggable) {
      handle = event.target.closest('[draggable="true"]')
    }

    // Verify it has the drag-handle data attribute
    if (!handle || !handle.hasAttribute('data-drag-handle')) {
      return
    }

    // Find the activity card that contains this handle
    const item = handle.closest('[data-activity-card]')

    if (!item) {
      return
    }

    // Store the dragged element reference
    this.draggedElement = item
    window.__draggedActivityCard = item
    window.__draggedActivitySourceContainer = this.element
    window.__draggedActivitySourcePhaseId = this.phaseIdValue || null

    // Set drag image to the entire activity card
    const rect = item.getBoundingClientRect()
    event.dataTransfer.setDragImage(item, event.clientX - rect.left, event.clientY - rect.top)

    // Set drag data with a marker to identify this as a reorder operation
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("application/x-activity-reorder", "true")
    const activityId = this.activityIdFor(item)
    if (!activityId) {
      return
    }
    event.dataTransfer.setData("application/x-activity-id", activityId)
  }

  dragEnd() {
    // Restore normal state with CSS
    if (this.draggedElement) {
      this.draggedElement.style.boxShadow = ""
      this.draggedElement.style.transform = ""
      this.draggedElement.style.transition = ""
      this.draggedElement.style.cursor = ""
    }

    // Update activity numbers only once at the end
    this.updateActivityNumbers()

    // Save new order to database without page reload
    this.saveOrder()

    // Dispatch custom event to notify other components about the reorder
    window.dispatchEvent(new CustomEvent('activity-list-changed', {
      detail: { type: 'reorder' }
    }))

    // Reset dragged element
    this.draggedElement = null
    window.__draggedActivityCard = null
    window.__draggedActivitySourceContainer = null
    window.__draggedActivitySourcePhaseId = null
  }

  dragOver(event) {
    // Check if this is a reorder operation by checking dataTransfer types
    const types = event.dataTransfer.types || []
    const isReorderOperation = types.includes("application/x-activity-reorder")

    const draggedElement = this.draggedElement || window.__draggedActivityCard

    // Only handle if this is a reorder operation
    if (!draggedElement || !isReorderOperation) {
      return
    }

    // Find if we're over an activity card
    const item = event.target.closest('[data-activity-card]')
    if (item && !this.element.contains(item)) {
      return
    }

    // Allow drop into empty container space
    if (!item) {
      event.preventDefault()
      event.stopPropagation()
      event.dataTransfer.dropEffect = "move"
      return
    }

    // Don't show drop zone on the dragged element itself
    if (item === draggedElement) {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = "move"

    // Determine if we should insert above or below based on mouse position
    const rect = item.getBoundingClientRect()
    const midpoint = rect.top + rect.height / 2
    const mouseY = event.clientY

    // Calculate the target position where the dragged element should be inserted
    let targetPosition
    if (mouseY < midpoint) {
      targetPosition = item
    } else {
      targetPosition = item.nextSibling
    }

    // Only reorder if the position has actually changed
    // Check if dragged element is already in the correct position
    if (targetPosition !== draggedElement && targetPosition !== draggedElement.nextSibling) {
      // Throttle reordering to prevent too many updates
      const now = Date.now()
      if (now - this.lastReorderTime >= this.reorderThrottle) {
        this.lastReorderTime = now
        // Perform instant reordering with FLIP animation
        this.instantReorder(targetPosition, draggedElement)
      }
    }
  }

  dragLeave() {
    // Not needed anymore since we're doing instant reordering
  }

  drop(event) {
    // Check if this is a reorder operation
    const types = event.dataTransfer.types || []
    const isReorderOperation = types.includes("application/x-activity-reorder")

    // Only handle reorder operations
    if (!isReorderOperation) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    const draggedElement = window.__draggedActivityCard
    if (!draggedElement) {
      return
    }

    // Cross-phase move (drop into a different container)
    if (!this.element.contains(draggedElement)) {
      const dropTarget = event.target.closest('[data-activity-card]')
      const rect = dropTarget ? dropTarget.getBoundingClientRect() : null
      const midpoint = rect ? rect.top + rect.height / 2 : null

      let targetPosition = null
      if (dropTarget && rect && midpoint !== null) {
        targetPosition = event.clientY < midpoint ? dropTarget : dropTarget.nextSibling
      }

      this.element.insertBefore(draggedElement, targetPosition)
      this.updateActivityNumbers()
      this.updateActivityNumbersFor(window.__draggedActivitySourceContainer)
      this.updateActivityCountBadge(this.element)
      this.updateActivityCountBadge(window.__draggedActivitySourceContainer)

      this.saveOrder()

      // Dispatch event for cross-phase move to notify other components
      window.dispatchEvent(new CustomEvent('activity-list-changed', {
        detail: { type: 'reorder' }
      }))
    }
  }

  instantReorder(targetPosition, draggedElement) {
    // FLIP Animation: First - Last - Invert - Play
    // Step 1: First - Record positions before DOM change
    const allCards = Array.from(this.element.querySelectorAll('[data-activity-card]'))
    const firstPositions = new Map()

    allCards.forEach(card => {
      const rect = card.getBoundingClientRect()
      firstPositions.set(card, {
        top: rect.top,
        height: rect.height
      })
    })

    // Step 2: Last - Make the DOM change
    this.element.insertBefore(draggedElement, targetPosition)

    // Step 3 & 4: Invert and Play - Animate to new positions
    // Get fresh card list after reordering
    const reorderedCards = Array.from(this.element.querySelectorAll('[data-activity-card]'))

    reorderedCards.forEach((card) => {
      const first = firstPositions.get(card)
      const last = card.getBoundingClientRect()

      // Calculate invert values
      const deltaY = first ? first.top - last.top : 0

      if (Math.abs(deltaY) > 1) { // Only animate if there's significant movement
        // Invert: Apply transform to put it back to old position
        card.style.transform = `translateY(${deltaY}px)`
        card.style.transition = 'none'
        card.style.willChange = 'transform'

        // Force a single reflow for all cards
        void card.offsetHeight

        // Play: Animate to final position
        card.style.transition = 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
        card.style.transform = 'translateY(0)'

        // Cleanup after animation
        const cleanup = () => {
          card.style.transition = ''
          card.style.transform = ''
          card.style.willChange = ''
          card.removeEventListener('transitionend', cleanup)
        }
        card.addEventListener('transitionend', cleanup, { once: true })
      } else {
        // Clean up transform even if no animation
        card.style.transform = ''
        card.style.transition = ''
        card.style.willChange = ''
      }
    })
  }

  updateActivityNumbers() {
    // Find all activity cards in the container and update their numbers
    const allCards = this.element.querySelectorAll('[data-activity-card]')
    allCards.forEach((card, index) => {
      const numberBadge = card.querySelector('[data-activity-number]')
      if (numberBadge) {
        numberBadge.textContent = index + 1
      }
    })
  }

  updateActivityNumbersFor(container) {
    if (!container) return

    const allCards = container.querySelectorAll('[data-activity-card]')
    allCards.forEach((card, index) => {
      const numberBadge = card.querySelector('[data-activity-number]')
      if (numberBadge) {
        numberBadge.textContent = index + 1
      }
    })
  }

  saveOrder() {
    // Get all activity cards in their current order
    const allCards = this.element.querySelectorAll('[data-activity-card]')
    const activityIds = Array.from(allCards)
      .map(card => this.activityIdFor(card))
      .filter(Boolean)

    // Don't send if no reorder URL configured
    if (!this.hasReorderUrlValue) {
      return
    }

    // Send to server
    fetch(this.reorderUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({
        activity_ids: activityIds,
        activity_phase_id: this.phaseIdValue || null
      })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.json()
    })
    .catch(error => {
      console.error('Error saving order:', error)
    })
  }

  updateActivityCountBadge(container) {
    if (!container) return

    const phaseContainer = container.closest('[data-phase-id]')
    if (!phaseContainer) return

    const countBadge = phaseContainer.querySelector('[data-activity-count]')
    if (!countBadge) return

    const activitiesContainer = phaseContainer.querySelector('[data-activities-container]')
    const activityCount = activitiesContainer ? activitiesContainer.querySelectorAll('[data-activity-card]').length : 0

    const countText = countBadge.querySelector('[data-activity-count-text]')
    if (countText) {
      const activityWord = activityCount === 1 ? 'activity' : 'activities'
      countText.textContent = `${activityCount} ${activityWord}`
    }

    countBadge.setAttribute('data-activity-count-value', activityCount)
    countBadge.style.display = activityCount > 0 ? '' : 'none'
  }

  activityIdFor(card) {
    return card.dataset.facilitatorActivityActivityIdValue || card.dataset.activityId
  }
}
